import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, Sparkles, ArrowRight, Phone, MessageSquare, KeyRound } from 'lucide-react';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0c49a145-2431-460e-8df4-1bea13020ea4/artifacts/j4k88xee_file_00000000c9b871f8a2879288a4f57131.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const { login, loginWithToken, language } = useAuth();
  const tr = t(language);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // OTP Login state
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' or 'phone'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [mockOtp, setMockOtp] = useState(''); // For testing
  const [sendingOtp, setSendingOtp] = useState(false);
  
  // Forgot Password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [mockResetCode, setMockResetCode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Error', { description: 'Please fill in all fields' });
      return;
    }
    
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid email or password';
      toast.error('Login failed', { description: message });
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!phone || phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    setSendingOtp(true);
    try {
      const response = await axios.post(`${API}/auth/otp/send`, { phone });
      setOtpSent(true);
      // For testing: show the mock OTP
      if (response.data.mock_otp) {
        setMockOtp(response.data.mock_otp);
        toast.success(`OTP sent! (Test mode: ${response.data.mock_otp})`);
      } else {
        toast.success('OTP sent to your phone!');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send OTP';
      toast.error(message);
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/otp/verify`, { phone, otp });
      
      // Use the token to log in
      if (response.data.token && response.data.user) {
        loginWithToken(response.data.token, response.data.user);
        toast.success(response.data.is_new_user ? 'Account created! Welcome!' : 'Welcome back!');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid OTP';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password handlers
  const handleSendResetCode = async () => {
    if (!resetEmail || !resetEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setResetLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
      setResetCodeSent(true);
      // Check if email was sent or if we got a code for testing
      if (response.data.email_sent) {
        toast.success('Kòd reset la voye nan imèl ou! Tcheke bwat imèl ou.');
      } else if (response.data.code) {
        setMockResetCode(response.data.code);
        toast.success(`Reset code: ${response.data.code} (Test mode)`);
      } else {
        toast.success('Reset code sent!');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send reset code';
      toast.error(message);
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetCode || resetCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: resetEmail,
        code: resetCode,
        new_password: newPassword
      });
      toast.success('Password reset successfully! You can now log in.');
      setShowForgotPassword(false);
      // Reset form
      setResetEmail('');
      setResetCode('');
      setNewPassword('');
      setConfirmNewPassword('');
      setResetCodeSent(false);
      setMockResetCode('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reset password';
      toast.error(message);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0a0a0f] text-white overflow-hidden" data-testid="login-page">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pink-500/30 rounded-full blur-[128px] animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-violet-500/30 rounded-full blur-[128px] animate-pulse delay-1000" />
        </div>
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center items-center p-12 w-full">
          <div className="max-w-lg text-center">
            <img src={LOGO_URL} alt="FityH" className="w-24 h-24 mx-auto mb-6 rounded-2xl" />
            <h1 className="text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-pink-500 to-violet-500 bg-clip-text text-transparent">
                FityH
              </span>
            </h1>
            <p className="text-2xl font-medium text-white/90 mb-4">
              The Hybrid Social Platform for Creators
            </p>
            <p className="text-lg text-white/60 mb-8">
              Connect. Share. Create. Earn.
            </p>
            
            {/* Features */}
            <div className="space-y-4 text-left">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium">AI-Powered Creation</p>
                  <p className="text-sm text-white/60">Generate videos & images with AI</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
                <div>
                  <p className="font-medium">Creator Economy</p>
                  <p className="text-sm text-white/60">Earn from subscriptions & tips</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img src={LOGO_URL} alt="FityH" className="w-16 h-16 mx-auto mb-4 rounded-xl" />
            <h1 className="text-3xl font-bold bg-gradient-to-r from-pink-500 to-violet-500 bg-clip-text text-transparent">
              FityH
            </h1>
            <p className="text-white/60 mt-1">Welcome back</p>
          </div>
          
          {/* Form Header */}
          <div className="hidden lg:block mb-8">
            <h2 className="text-3xl font-bold mb-2">Welcome back</h2>
            <p className="text-white/60">Log in to continue to FityH</p>
          </div>
          
          {/* Login Method Toggle */}
          <div className="flex rounded-xl bg-white/5 p-1 mb-6">
            <button
              type="button"
              onClick={() => { setLoginMethod('email'); setOtpSent(false); }}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                loginMethod === 'email' 
                  ? 'bg-gradient-to-r from-pink-500 to-violet-500 text-white' 
                  : 'text-white/60 hover:text-white'
              }`}
              data-testid="login-email-tab"
            >
              <Mail className="w-4 h-4" />
              Email
            </button>
            <button
              type="button"
              onClick={() => setLoginMethod('phone')}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                loginMethod === 'phone' 
                  ? 'bg-gradient-to-r from-pink-500 to-violet-500 text-white' 
                  : 'text-white/60 hover:text-white'
              }`}
              data-testid="login-phone-tab"
            >
              <Phone className="w-4 h-4" />
              Phone (OTP)
            </button>
          </div>
          
          {/* Email Login Form */}
          {loginMethod === 'email' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/80">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-12 h-14 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500 focus:ring-pink-500/20"
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-white/80">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-12 pr-12 h-14 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500 focus:ring-pink-500/20"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              {/* Forgot Password Link */}
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-pink-400 hover:text-pink-300 transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 rounded-xl font-bold text-lg bg-gradient-to-r from-pink-500 to-violet-500 hover:from-pink-600 hover:to-violet-600 text-white shadow-lg shadow-pink-500/25 transition-all hover:shadow-pink-500/40 hover:scale-[1.02]"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Log In
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
          )}
          
          {/* Phone OTP Login Form */}
          {loginMethod === 'phone' && (
            <form onSubmit={handleVerifyOtp} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-white/80">Phone Number</Label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={otpSent}
                    className="pl-12 h-14 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500 focus:ring-pink-500/20 disabled:opacity-50"
                    data-testid="login-phone-input"
                  />
                </div>
              </div>
              
              {!otpSent ? (
                <Button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={sendingOtp || !phone}
                  className="w-full h-14 rounded-xl font-bold text-lg bg-gradient-to-r from-pink-500 to-violet-500 hover:from-pink-600 hover:to-violet-600 text-white shadow-lg shadow-pink-500/25 transition-all hover:shadow-pink-500/40 hover:scale-[1.02]"
                  data-testid="send-otp-btn"
                >
                  {sendingOtp ? (
                    <span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <MessageSquare className="w-5 h-5 mr-2" />
                      Send OTP Code
                    </>
                  )}
                </Button>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="otp" className="text-white/80">Enter OTP Code</Label>
                    <div className="relative">
                      <MessageSquare className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                      <Input
                        id="otp"
                        type="text"
                        placeholder="123456"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        maxLength={6}
                        className="pl-12 h-14 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500 focus:ring-pink-500/20 text-center text-2xl tracking-widest"
                        data-testid="login-otp-input"
                      />
                    </div>
                    {mockOtp && (
                      <p className="text-xs text-green-400 text-center">
                        Test Mode: Your OTP is <strong>{mockOtp}</strong>
                      </p>
                    )}
                  </div>
                  
                  <Button
                    type="submit"
                    disabled={loading || otp.length !== 6}
                    className="w-full h-14 rounded-xl font-bold text-lg bg-gradient-to-r from-pink-500 to-violet-500 hover:from-pink-600 hover:to-violet-600 text-white shadow-lg shadow-pink-500/25 transition-all hover:shadow-pink-500/40 hover:scale-[1.02]"
                    data-testid="verify-otp-btn"
                  >
                    {loading ? (
                      <span className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        Verify & Login
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                  
                  <button
                    type="button"
                    onClick={() => { setOtpSent(false); setOtp(''); setMockOtp(''); }}
                    className="w-full text-center text-white/60 hover:text-white text-sm"
                  >
                    Use different number
                  </button>
                </>
              )}
            </form>
          )}
          
          {/* Divider */}
          <div className="flex items-center gap-4 my-8">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-white/40 text-sm">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>
          
          {/* Register Link */}
          <p className="text-center text-white/60">
            Don't have an account?{' '}
            <Link 
              to="/register" 
              className="text-pink-500 font-semibold hover:text-pink-400 transition-colors"
              data-testid="register-link"
            >
              Sign up free
            </Link>
          </p>
          
          {/* Back to Home */}
          <Link
            to="/"
            className="flex items-center justify-center gap-2 mt-6 text-white/40 hover:text-white/80 transition-colors text-sm"
          >
            <ArrowRight className="w-4 h-4 rotate-180" />
            Back to home
          </Link>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-pink-500" />
              Reset Password
            </DialogTitle>
            <DialogDescription className="text-white/60">
              {resetCodeSent 
                ? 'Enter the code sent to your email and create a new password.'
                : 'Enter your email to receive a password reset code.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {!resetCodeSent ? (
              <>
                <div className="space-y-2">
                  <Label className="text-white/80">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="pl-12 h-12 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500"
                      data-testid="reset-email-input"
                    />
                  </div>
                </div>
                
                <Button
                  onClick={handleSendResetCode}
                  disabled={resetLoading}
                  className="w-full h-12 rounded-xl bg-gradient-to-r from-pink-500 to-violet-500 hover:from-pink-600 hover:to-violet-600"
                  data-testid="send-reset-code-btn"
                >
                  {resetLoading ? (
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Send Reset Code'
                  )}
                </Button>
              </>
            ) : (
              <>
                {mockResetCode ? (
                  <div className="p-3 rounded-xl bg-pink-500/20 border border-pink-500/30 text-center">
                    <p className="text-sm text-pink-300">Test Mode: Your code is <span className="font-bold">{mockResetCode}</span></p>
                  </div>
                ) : (
                  <div className="p-3 rounded-xl bg-green-500/20 border border-green-500/30 text-center">
                    <p className="text-sm text-green-300">✉️ Kòd la voye nan imèl ou: <span className="font-bold">{resetEmail}</span></p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label className="text-white/80">Reset Code</Label>
                  <Input
                    type="text"
                    placeholder="123456"
                    maxLength={6}
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ''))}
                    className="h-12 rounded-xl bg-white/5 border-white/10 text-white text-center text-2xl tracking-widest placeholder:text-white/30 focus:border-pink-500"
                    data-testid="reset-code-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white/80">New Password</Label>
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="h-12 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500"
                    data-testid="new-password-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white/80">Confirm New Password</Label>
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    className="h-12 rounded-xl bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-pink-500"
                    data-testid="confirm-new-password-input"
                  />
                </div>
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setResetCodeSent(false);
                      setResetCode('');
                      setMockResetCode('');
                    }}
                    className="flex-1 h-12 rounded-xl border-white/20 text-white hover:bg-white/10"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleResetPassword}
                    disabled={resetLoading}
                    className="flex-1 h-12 rounded-xl bg-gradient-to-r from-pink-500 to-violet-500 hover:from-pink-600 hover:to-violet-600"
                    data-testid="reset-password-btn"
                  >
                    {resetLoading ? (
                      <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Reset Password'
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
